import { forwardRef, useImperativeHandle } from 'react';
import { MAIN_USE_OF_SUBSCRIPTION } from '../../../../common/enums.js';
import { t } from '../../../../common/i18n/index.js';
import { Dropdown } from '../../../../common/react-hook-form/components/Dropdown.js';
import { ProductOfferingCharacteristicSelectorCoCo } from '../../../ProductOfferingAddonCoCo/ProductOfferingCharacteristicSelectorCoCo.js';
import { useCheckoutContext } from '../../context/CheckoutContextCoCo.js';
import { PlanProductRuntimeCharacteristic } from '../../utils/characteristicsFlagsUtilsCoCo.js';
import {
  getCharacteristicOptions,
  getDefaultCharacteristicValue,
  getSelectedCharacteristicValue,
} from '../../utils/runtimeCharacteristicsCoCo.js';
import { ContactOrPurposeSectionCoCo } from '../shared/ContactOrPurposeSectionCoCo.js';
import { InstallationContactSectionCoCo } from '../shared/InstallationContactSectionCoCo.js';
import type { ConfigHandle, ProductConfigProps } from '../productConfigTypesCoCo.js';

import './FixedBroadbandProductConfigCoCo.scss';

export const FixedBroadbandProductConfigCoCo = forwardRef<ConfigHandle, ProductConfigProps>(
  (
    {
      basketItem,
      orderItem,
      schema,
      contactFormRefs,
      installationContactRefs,
      contactsUpdateMethods,
      productGroupCartItemIds = [],
      isProductGroupFirstUnit = false,
      onProductOfferingRuntimeCharacteristicChange,
    },
    ref
  ) => {
    const { selectedProductOffers } = useCheckoutContext();
    const selectedPlanProductOffering = selectedProductOffers.find(
      selectedOffer => selectedOffer.id === basketItem.id
    )?.planProductOffering;
    const basketPlanProductOffering = basketItem.planProductOffering;
    const planProductOffering = basketPlanProductOffering
      ? {
          ...selectedPlanProductOffering,
          ...basketPlanProductOffering,
          productOfferingRuntimeCharacteristic:
            basketPlanProductOffering.productOfferingRuntimeCharacteristic ??
            selectedPlanProductOffering?.productOfferingRuntimeCharacteristic,
          productOfferingCharacteristic:
            basketPlanProductOffering.productOfferingCharacteristic ??
            selectedPlanProductOffering?.productOfferingCharacteristic,
        }
      : selectedPlanProductOffering;
    const runtimeCharacteristics = planProductOffering?.productOfferingRuntimeCharacteristic;
    const availableRuntimeCharacteristics = runtimeCharacteristics ?? [];
    const fiberCharacteristic = availableRuntimeCharacteristics.find(
      characteristic => characteristic.name === PlanProductRuntimeCharacteristic.FIBER_MODE
    );
    const selectedFiberMode = getSelectedCharacteristicValue(
      basketItem.productOfferingRuntimeCharacteristic,
      PlanProductRuntimeCharacteristic.FIBER_MODE
    );
    const connectionUseCharacteristics = runtimeCharacteristics?.some(
      characteristic => characteristic.name === MAIN_USE_OF_SUBSCRIPTION
    )
      ? runtimeCharacteristics
      : planProductOffering?.productOfferingCharacteristic;
    const connectionUseLabel = t.AWAM('What is the connection used for');
    const connectionUseOptions = getCharacteristicOptions(
      connectionUseCharacteristics,
      MAIN_USE_OF_SUBSCRIPTION
    ).filter(option => option.value !== 'please select one value');
    useImperativeHandle(
      ref,
      () => ({
        validate: async () => true,
        scrollToFirstError: () => undefined,
        getErrorCount: () => 0,
      }),
      []
    );
    return (
      <div
        className="fixed-broadband-contact-config-coco"
        id={`fixed-broadband-config-${orderItem.basketItemId}-${orderItem.basketItemIndex}`}
      >
        <div className="fixed-broadband-contact-config-coco__contact-section">
          <ContactOrPurposeSectionCoCo
            schema={{ ...schema, needsContact: true }}
            orderItem={orderItem}
            contactFormRefs={contactFormRefs}
            contactsUpdateMethods={contactsUpdateMethods}
            isMandatory={true}
            showAccordion={true}
            showUserContact={true}
            contentBeforePurposeOfUse={
              <Dropdown
                className="of-fixed-broadband-connection-use"
                name={`additionalParameters.${MAIN_USE_OF_SUBSCRIPTION}`}
                label={connectionUseLabel}
                items={connectionUseOptions}
              />
            }
            contentBeforeCostCenter={
              schema.needsInstallationContact && (
                <InstallationContactSectionCoCo
                  ref={element => {
                    if (installationContactRefs) {
                      installationContactRefs.current[`${orderItem.basketItemId}-${orderItem.basketItemIndex}`] =
                        element;
                    }
                  }}
                  orderItem={orderItem}
                  contactsUpdateMethods={contactsUpdateMethods}
                />
              )
            }
          />
        </div>
        {isProductGroupFirstUnit && fiberCharacteristic && (
          <div className="fixed-broadband-contact-config-coco__fiber-section">
            <ProductOfferingCharacteristicSelectorCoCo
              characteristics={[fiberCharacteristic]}
              characteristicName={PlanProductRuntimeCharacteristic.FIBER_MODE}
              heading={t.RZQ4('Fiber type')}
              description={t.X04A(
                'Select the fiber type for your connection. Contact your property manager if you are unsure about the fiber type.'
              )}
              selectedValue={
                selectedFiberMode ??
                getDefaultCharacteristicValue(
                  availableRuntimeCharacteristics,
                  PlanProductRuntimeCharacteristic.FIBER_MODE
                )
              }
              onValueChange={value => onProductOfferingRuntimeCharacteristicChange?.(productGroupCartItemIds, value)}
            />
          </div>
        )}
      </div>
    );
  }
);

FixedBroadbandProductConfigCoCo.displayName = 'FixedBroadbandProductConfigCoCo';
